<template>
  <div>

    <div class="section-container" :class="isDarkMode ? 'dark' : 'light'">
      <div class="caption grey--text mb-1">
        Documents Expression — JS body returning an array of <code>{filename, contentType, content}</code> objects.
        Helpers: <code>getDoc(fieldId)</code>, <code>getDocs([fieldIds])</code>, <code>getValue(fieldId)</code>, <code>getTaskHeader()</code>, <code>getTaskStepsAndValues()</code>.
      </div>
      <div class="preview-box" :class="isDarkMode ? 'dark' : 'light'">
        {{ truncate(localValue.docsExpression) || 'return [];' }}
      </div>
      <v-btn small flat color="secondary" class="ma-0 mt-1" @click="editingDocs = true">Edit Documents Expression</v-btn>
    </div>

    <div class="section-container mt-2" :class="isDarkMode ? 'dark' : 'light'">
      <div class="c-d-flex c-align-center mb-1">
        <div class="caption grey--text c-flex-grow-1">Stages (run sequentially; perDoc stages fan out in parallel across docs)</div>
        <v-btn small flat color="secondary" class="ma-0 mr-1" @click="addStage('llm')">+ LLM Stage</v-btn>
        <v-btn small flat color="info" class="ma-0" @click="addStage('code')">+ Code Stage</v-btn>
      </div>

      <div v-if="!stages.length" class="caption grey--text text--darken-1 py-2 text-xs-center">
        No stages yet. Add an LLM stage to extract data, or a Code stage to render output.
      </div>

      <div v-for="(stage, idx) in stages" :key="stage.id + '_' + idx" class="stage-row" :class="isDarkMode ? 'dark' : 'light'">
        <div class="c-d-flex c-align-center">
          <div class="stage-idx">{{ idx + 1 }}</div>
          <v-chip small :color="stage.kind === 'code' ? 'info' : 'success'" text-color="white" class="ma-0 mr-2">
            {{ stage.kind === 'code' ? 'CODE' : 'LLM' }}
          </v-chip>
          <div class="c-flex-grow-1">
            <div class="stage-label">{{ stage.label || stage.id || '(untitled)' }}</div>
            <div class="stage-meta">
              <span>id: <code>{{ stage.id || '—' }}</code></span>
              <span v-if="stage.kind === 'llm'" class="pl-2">iterate: {{ stage.iterate || 'none' }}</span>
              <span v-if="stage.kind === 'llm' && stage.outputJson" class="pl-2">json</span>
              <span v-if="stage.kind === 'llm' && stage.retry && stage.retry.maxAttempts > 1" class="pl-2">retry×{{ stage.retry.maxAttempts }}</span>
            </div>
          </div>
          <v-btn icon small flat class="ma-0" :disabled="idx === 0" @click="moveStage(idx, -1)"><v-icon small>arrow_upward</v-icon></v-btn>
          <v-btn icon small flat class="ma-0" :disabled="idx === stages.length - 1" @click="moveStage(idx, 1)"><v-icon small>arrow_downward</v-icon></v-btn>
          <v-btn icon small flat class="ma-0" @click="editStage(idx)"><v-icon small>edit</v-icon></v-btn>
          <v-btn icon small flat class="ma-0" @click="removeStage(idx)"><v-icon small color="error">delete</v-icon></v-btn>
        </div>
      </div>
    </div>

    <div class="section-container mt-2" :class="isDarkMode ? 'dark' : 'light'">
      <div class="c-d-flex c-align-center">
        <div style="width: 50%" class="pr-2">
          <v-autocomplete
              dense hide-details single-line
              v-model="finalStage"
              :items="stageOptions"
              item-text="label" item-value="id"
              label="Final Stage (its output is the field value)"
          />
        </div>
        <div style="width: 50%">
          <v-autocomplete
              dense hide-details single-line
              v-model="outputKind"
              :items="outputKindOptions"
              item-text="description" item-value="id"
              label="Output Kind"
          />
        </div>
      </div>
    </div>

    <a-i-prompt-editor
        v-if="editingDocs"
        :shown="true"
        v-model="localValue.docsExpression"
        title="Documents Expression"
        @dismiss="editingDocs = false; emit()"
    />

    <chain-stage-dialog
        v-if="editingStageIdx !== null"
        :shown="true"
        :value="editingStage"
        :is-new="editingStageIsNew"
        :models="models"
        @dismiss="cancelEditStage"
        @save="commitEditStage"
    />

  </div>
</template>

<script>
import AIPromptEditor from "./AIPromptEditor";
import ChainStageDialog from "./ChainStageDialog";

export default {
  components: { AIPromptEditor, ChainStageDialog },
  props: {
    value: { type: Object, default: () => ({}) },
    models: { type: Array, default: () => [] },
  },
  data() {
    return {
      localValue: this.normalise(this.value),
      editingDocs: false,
      editingStageIdx: null,
      editingStage: null,
      editingStageIsNew: false,
    }
  },
  watch: {
    value: {
      immediate: false,
      handler(v) { this.localValue = this.normalise(v) }
    }
  },
  computed: {
    isDarkMode() { return this.$store.state.darkTheme },
    stages() { return this.localValue.stages || [] },
    stageOptions() {
      return this.stages.map(s => ({ id: s.id, label: `${s.label || s.id} (${s.kind})` }))
    },
    outputKindOptions() {
      return [
        { id: 'html', description: 'HTML' },
        { id: 'md', description: 'Markdown' },
        { id: 'json', description: 'JSON (field update)' },
      ]
    },
    finalStage: {
      get() { return this.localValue.finalStage || null },
      set(v) { this.$set(this.localValue, 'finalStage', v); this.emit() }
    },
    outputKind: {
      get() { return this.localValue.outputKind || 'html' },
      set(v) { this.$set(this.localValue, 'outputKind', v); this.emit() }
    }
  },
  methods: {
    normalise(v) {
      const base = { docsExpression: '', stages: [], finalStage: null, outputKind: 'html' }
      const cloned = JSON.parse(JSON.stringify(v || {}))
      return Object.assign({}, base, cloned)
    },
    emit() {
      this.$emit('input', JSON.parse(JSON.stringify(this.localValue)))
    },
    truncate(s) {
      if (!s) return ''
      const t = String(s).replace(/\s+/g, ' ').trim()
      return t.length > 180 ? t.slice(0, 180) + '…' : t
    },
    addStage(kind) {
      const existingIds = new Set(this.stages.map(s => s.id))
      let base = kind === 'code' ? 'render' : 'stage'
      let id = base, n = 1
      while (existingIds.has(id)) { id = `${base}${++n}` }
      const stage = {
        id,
        label: kind === 'code' ? 'Render' : 'New Stage',
        kind,
        iterate: 'none',
        systemPrompt: '',
        prompt: '',
        model: null,
        maxTokens: 4000,
        maxThinkingTokens: null,
        outputJson: false,
        validator: '',
        retry: { maxAttempts: 1 },
        code: kind === 'code' ? 'return { html: "" };' : '',
      }
      this.editingStage = stage
      this.editingStageIdx = this.stages.length
      this.editingStageIsNew = true
    },
    editStage(idx) {
      this.editingStage = JSON.parse(JSON.stringify(this.stages[idx]))
      this.editingStageIdx = idx
      this.editingStageIsNew = false
    },
    cancelEditStage() {
      this.editingStageIdx = null
      this.editingStage = null
      this.editingStageIsNew = false
    },
    commitEditStage(stage) {
      const stages = this.stages.slice()
      if (this.editingStageIsNew) stages.push(stage)
      else stages.splice(this.editingStageIdx, 1, stage)
      this.$set(this.localValue, 'stages', stages)
      if (!this.localValue.finalStage && stages.length) this.$set(this.localValue, 'finalStage', stages[stages.length - 1].id)
      this.cancelEditStage()
      this.emit()
    },
    removeStage(idx) {
      const stages = this.stages.slice()
      const [removed] = stages.splice(idx, 1)
      this.$set(this.localValue, 'stages', stages)
      if (this.localValue.finalStage === removed.id) {
        this.$set(this.localValue, 'finalStage', stages.length ? stages[stages.length - 1].id : null)
      }
      this.emit()
    },
    moveStage(idx, delta) {
      const j = idx + delta
      if (j < 0 || j >= this.stages.length) return
      const stages = this.stages.slice()
      const [item] = stages.splice(idx, 1)
      stages.splice(j, 0, item)
      this.$set(this.localValue, 'stages', stages)
      this.emit()
    }
  }
}
</script>

<style scoped>
.section-container {
  padding: 8px;
  border-radius: 4px;
}
.section-container.dark { border: 1px solid rgba(255,255,255,0.12); background: rgba(0,0,0,0.2); }
.section-container.light { border: 1px solid rgba(0,0,0,0.12); background: rgba(0,0,0,0.04); }
.preview-box {
  font-family: 'Courier New', monospace;
  font-size: 11px;
  padding: 6px 8px;
  border-radius: 3px;
  min-height: 28px;
  max-height: 60px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.preview-box.dark { background: rgba(50,50,50,0.5); color: rgba(255,255,255,0.87); border: 1px solid rgba(255,255,255,0.12); }
.preview-box.light { background: rgba(0,0,0,0.06); color: rgba(0,0,0,0.87); border: 1px solid rgba(0,0,0,0.08); }
.stage-row {
  padding: 6px 8px;
  margin-top: 4px;
  border-radius: 3px;
}
.stage-row.dark { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); }
.stage-row.light { background: #fff; border: 1px solid rgba(0,0,0,0.08); }
.stage-idx {
  width: 22px; height: 22px;
  border-radius: 50%;
  background: rgba(0,0,0,0.08);
  color: inherit;
  font-size: 11px; font-weight: bold;
  display: flex; align-items: center; justify-content: center;
  margin-right: 8px;
}
.stage-label { font-weight: bold; font-size: 13px; }
.stage-meta { font-size: 10px; opacity: 0.7; }
</style>
